// frontend/packages/business-console/src/pages/Quotes.jsx
//
// Work item 2.13 — Quotes list page (`/business/:slug/quotes`).
// Spec source: `docs/design/quotes-invoices-console.md` §3.3.
//
// Design:
//   - Desktop: MUI `Table` with columns Number / Customer / State /
//     Total / Created. Row click → detail page.
//   - Mobile (≤sm): list of cards, tap-through.
//   - Top: title + "New quote" CTA + state-chip filter row + search.
//   - Empty state with "Create your first quote" CTA.
//   - Cursor pagination via "Load more" at bottom.
//   - Demo mode (`?demo=1`) renders fixtures from `Quotes/demoData.js`.

import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { BusinessContext } from '../contexts/BusinessContext';
import { useQuotesList } from './Quotes/useQuotesList';
import { convertQuoteToInvoice, deleteQuoteDraft } from '../services/quoteService';
import { fetchPipelinePulse } from '../services/pulseService';
import {
  useTokens,
  Surface,
  StatusPill,
  PageHeader,
  MetricBarCards,
} from '../components/primitives';

const STATE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Drafts' },
  { key: 'sent', label: 'Sent' },
  { key: 'viewed', label: 'Viewed' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'declined', label: 'Declined' },
  { key: 'expired', label: 'Expired' },
];

// Quote state -> shared StatusPill kind (semantic color).
const STATE_KIND = {
  draft: 'neutral',
  sent: 'info',
  viewed: 'warning',
  accepted: 'success',
  declined: 'error',
  expired: 'neutral',
  converted: 'success',
  void: 'neutral',
};

function StatePill({ state }) {
  return (
    <StatusPill
      size="sm"
      kind={STATE_KIND[state] || 'neutral'}
      label={state}
      sx={{ textTransform: 'capitalize' }}
    />
  );
}

function fmtUsd(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function formatRelative(input) {
  if (!input) return '—';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  const days = Math.round((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 1) return 'today';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days / 7)}w ago`;
  return d.toLocaleDateString();
}

export default function Quotes() {
  const { business } = useContext(BusinessContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  // ?state=X / ?q=Y hydrate the filter chips on first paint so Pulse
  // deep links (e.g. "Outstanding $X" → /quotes?state=accepted) land
  // pre-filtered. We also write back on chip clicks so the URL is
  // shareable and the back button restores filter state.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialState = searchParams.get('state');
  const initialQ = searchParams.get('q') || '';
  const allowedStates = useMemo(
    () => new Set(STATE_FILTERS.map((f) => f.key)),
    []
  );
  const [stateFilter, setStateFilter] = useState(
    initialState && allowedStates.has(initialState) ? initialState : 'all'
  );
  const [search, setSearch] = useState(initialQ);
  const [debouncedSearch, setDebouncedSearch] = useState(initialQ);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (stateFilter === 'all') next.delete('state');
    else next.set('state', stateFilter);
    if (!debouncedSearch) next.delete('q');
    else next.set('q', debouncedSearch);
    // Only call setSearchParams if something actually changed to avoid
    // an infinite render loop.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [stateFilter, debouncedSearch, searchParams, setSearchParams]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const {
    items,
    hasMore,
    loading,
    loadingMore,
    error,
    loadMore,
    refresh,
    isDemo,
  } = useQuotesList({ state: stateFilter, q: debouncedSearch });

  const hasFilterActive = stateFilter !== 'all' || debouncedSearch.length > 0;

  // Pipeline stat strip (redesign), reusing the existing Pulse pipeline
  // aggregate. Non-blocking; demo mode skips it.
  const [pulse, setPulse] = useState(null);
  useEffect(() => {
    if (isDemo) return undefined;
    let cancelled = false;
    fetchPipelinePulse()
      .then((d) => {
        if (!cancelled) setPulse(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isDemo]);
  const stripItems = useMemo(() => {
    if (!pulse) return [];
    return [
      { label: 'Draft', value: pulse.draftCount || 0, bar: 'grayBorder' },
      { label: 'Sent', value: pulse.sentCount || 0, bar: 'blue' },
      { label: 'Viewed', value: pulse.viewedCount || 0, bar: 'amber' },
      { label: 'Accepted', value: pulse.acceptedCount || 0, bar: 'green' },
      {
        label: 'Win rate',
        value: `${pulse.conversionPct ?? 0}%`,
        sub: 'quotes accepted',
        gradient: true,
      },
    ];
  }, [pulse]);

  // D17 convert — one-tap "Convert to invoice" on accepted estimates in the
  // list, so the operator doesn't have to open the detail. Reuses the proven
  // /convert path (pre-fills + links + tags the estimate converted), then
  // lands on the new invoice.
  const [convertingId, setConvertingId] = useState(null);
  const handleConvert = async (quoteId) => {
    if (convertingId) return;
    setConvertingId(quoteId);
    try {
      const result = await convertQuoteToInvoice(quoteId);
      const newId = result?.invoice?._id;
      if (newId) {
        navigate(`${basePath}/invoices/${newId}/edit`);
        return;
      }
      refresh();
    } catch {
      refresh();
    } finally {
      setConvertingId(null);
    }
  };

  // Delete (soft, recoverable + audited server-side). A confirm guards it
  // since it removes the estimate from the list. Works in any state, so
  // mistaken or test estimates can be cleaned up.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await deleteQuoteDraft(deleteTarget._id);
      setDeleteTarget(null);
      refresh();
    } catch {
      // Leave the dialog open on failure; refresh keeps the list honest.
      refresh();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: 1280,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <PageHeader
        title="Estimates"
        meta={
          <Typography
            sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}
          >
            Estimates you&apos;ve drafted, sent, and accepted.
            {isDemo ? '  ·  Demo mode' : ''}
          </Typography>
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={refresh}
                disabled={loading}
                aria-label="Refresh estimates"
              >
                <RefreshRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              component={RouterLink}
              to={`${basePath}/quotes/drafts`}
              sx={{ textTransform: 'none' }}
            >
              Offline drafts
            </Button>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              component={RouterLink}
              to={`${basePath}/quotes/new`}
            >
              New estimate
            </Button>
          </Stack>
        }
      />

      {stripItems.length ? (
        <MetricBarCards cards={stripItems} sx={{ mb: 1.5 }} />
      ) : null}

      {/* ── Filter chips + search ───────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: 'wrap', gap: 1, rowGap: 1 }}
        >
          {STATE_FILTERS.map((f) => (
            <Chip
              key={f.key}
              label={f.label}
              size="small"
              clickable
              color={stateFilter === f.key ? 'primary' : 'default'}
              variant={stateFilter === f.key ? 'filled' : 'outlined'}
              onClick={() => setStateFilter(f.key)}
            />
          ))}
        </Stack>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search number or customer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 260 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* ── Error banner ────────────────────────────────────────── */}
      {error ? (
        <Alert severity="error" onClose={() => refresh()}>
          {error?.response?.data?.error ||
            error?.message ||
            "Couldn't load estimates."}
        </Alert>
      ) : null}

      {/* ── List body ───────────────────────────────────────────── */}
      {loading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <EmptyState hasFilterActive={hasFilterActive} basePath={basePath} />
      ) : isMobile ? (
        <QuoteCardList
          items={items}
          basePath={basePath}
          navigate={navigate}
          onConvert={handleConvert}
          convertingId={convertingId}
        />
      ) : (
        <QuoteTable
          items={items}
          basePath={basePath}
          navigate={navigate}
          onRequestDelete={setDeleteTarget}
        />
      )}

      {/* ── Pagination ──────────────────────────────────────────── */}
      {hasMore ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button
            variant="outlined"
            onClick={loadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={14} /> : null}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      ) : null}

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Delete {deleteTarget?.number || 'estimate'}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the estimate from your list. If it was already sent,
            the link the customer received stops working. It can be restored by
            support.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : null}
          >
            {deleting ? 'Deleting' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function LoadingState() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        py: 8,
      }}
    >
      <CircularProgress />
    </Box>
  );
}

function EmptyState({ hasFilterActive, basePath }) {
  return (
    <Surface
      sx={{
        borderStyle: 'dashed',
        py: 6,
        px: 3,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <RequestQuoteOutlinedIcon
        sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.6 }}
      />
      {hasFilterActive ? (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            No estimates match this filter
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', maxWidth: 380 }}
          >
            Try clearing the search or picking a different state.
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            No estimates yet
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', maxWidth: 380 }}
          >
            Estimates you create will appear here. Send one to a customer and
            the activity will flow through Pulse and the CRM.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            component={RouterLink}
            to={`${basePath}/quotes/new`}
          >
            Create your first estimate
          </Button>
        </>
      )}
    </Surface>
  );
}

function QuoteTable({ items, basePath, navigate, onRequestDelete }) {
  const t = useTokens();
  return (
    <TableContainer component={Surface} sx={{ overflow: 'hidden' }}>
      <Table
        size="small"
        sx={{
          '& th': {
            fontWeight: 800,
            fontSize: 10,
            letterSpacing: '.4px',
            textTransform: 'uppercase',
            color: t.faint,
            bgcolor: t.tableHead,
            borderBottom: `1px solid ${t.hairline}`,
          },
          '& td': { borderBottom: `1px solid ${t.hairline}`, color: t.text },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 110 }}>Number</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell sx={{ width: 120 }}>State</TableCell>
            <TableCell align="right" sx={{ width: 130 }}>
              Total
            </TableCell>
            <TableCell sx={{ width: 110 }}>Created</TableCell>
            <TableCell sx={{ width: 44 }} aria-label="Actions" />
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((q) => (
            <TableRow
              key={q._id}
              hover
              onClick={() => navigate(`${basePath}/quotes/${q._id}`)}
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: t.subtle } }}
            >
              <TableCell sx={{ fontWeight: 700, color: t.blue }}>
                {q.number}
              </TableCell>
              <TableCell>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, color: t.text }}
                >
                  {q.customerName || '—'}
                </Typography>
                {q.title ? (
                  <Typography sx={{ fontSize: 11.5, color: t.muted }}>
                    {q.title}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell>
                <StatePill state={q.state} />
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}
              >
                {fmtUsd(q.total)}
              </TableCell>
              <TableCell sx={{ color: t.faint }}>
                {formatRelative(q.createdAt)}
              </TableCell>
              <TableCell padding="none" align="center">
                <Tooltip title="Delete estimate">
                  <IconButton
                    size="small"
                    aria-label={`Delete ${q.number}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestDelete?.(q);
                    }}
                  >
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function QuoteCardList({ items, basePath, navigate, onConvert, convertingId }) {
  return (
    <Stack spacing={1.5}>
      {items.map((q) => (
        <Surface
          key={q._id}
          interactive
          onClick={() => navigate(`${basePath}/quotes/${q._id}`)}
          sx={{ p: 2 }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              >
                {q.number}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mt: 0.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {q.customerName || '—'}
              </Typography>
              {q.title ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {q.title}
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtUsd(q.total)}
              </Typography>
              <Box
                sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}
              >
                <StatePill state={q.state} />
              </Box>
            </Box>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', mt: 1, display: 'block' }}
          >
            Created {formatRelative(q.createdAt)}
          </Typography>
          {onConvert && q.state === 'accepted' ? (
            <Button
              size="small"
              variant="outlined"
              fullWidth
              disabled={convertingId === q._id}
              onClick={(e) => {
                e.stopPropagation();
                onConvert(q._id);
              }}
              sx={{ mt: 1.5, textTransform: 'none' }}
            >
              {convertingId === q._id ? 'Converting…' : 'Convert to invoice'}
            </Button>
          ) : null}
        </Surface>
      ))}
    </Stack>
  );
}
